import React, { useMemo, useState } from 'react';
import { Chip, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { InvoiceStatus, InvoiceStatuses } from '../../types/invoice-status.type';
import { INVOICE_STATUS_LABELS } from '../../constants/invoice-status-labels.constant';

type ChipStyle = { backgroundColor: string; color: string };

const getChipStyle = (status: InvoiceStatus): ChipStyle => {
  switch (status) {
    case InvoiceStatuses.DRAFT:
      return { backgroundColor: '#f0f0f0', color: '#000000' };
    case InvoiceStatuses.SENT:
      return { backgroundColor: '#2196f3', color: '#ffffff' };
    case InvoiceStatuses.PAID:
      return { backgroundColor: '#4caf50', color: '#ffffff' };
    case InvoiceStatuses.CANCELLED:
      return { backgroundColor: '#f44336', color: '#ffffff' };
    default:
      return { backgroundColor: '#f0f0f0', color: '#000000' };
  }
};

const ALLOWED_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  [InvoiceStatuses.DRAFT]: [InvoiceStatuses.SENT, InvoiceStatuses.CANCELLED],
  [InvoiceStatuses.SENT]: [InvoiceStatuses.PAID, InvoiceStatuses.CANCELLED],
  [InvoiceStatuses.PAID]: [InvoiceStatuses.CANCELLED],
  [InvoiceStatuses.CANCELLED]: [InvoiceStatuses.PAID],
};

const useStyles = makeStyles(() => ({
  root: (props: ChipStyle) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,
    '&:hover, &:focus': {
      backgroundColor: props.backgroundColor,
    },
  }),
}));

type Props = {
  status: InvoiceStatus;
  size?: 'small' | 'medium';
  onChange?: (nextStatus: InvoiceStatus) => void | Promise<void>;
};

export function InvoiceStatusChip({ status, size = 'small', onChange }: Props) {
  const { label, backgroundColor, color } = useMemo(
    () => ({ label: INVOICE_STATUS_LABELS[status], ...getChipStyle(status) }),
    [status],
  );
  const classes = useStyles({ backgroundColor, color });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const allowedNext = ALLOWED_TRANSITIONS[status];
  const isInteractive = Boolean(onChange) && allowedNext.length > 0;

  if (!isInteractive) {
    return <Chip className={classes.root} label={label} size={size} />;
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = async (nextStatus: InvoiceStatus) => {
    setAnchorEl(null);
    await onChange?.(nextStatus);
  };

  return (
    <>
      <Chip className={classes.root} label={label} size={size} onClick={handleOpen} />
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {allowedNext.map((nextStatus) => (
          <MenuItem key={nextStatus} onClick={() => handleSelect(nextStatus)}>
            {INVOICE_STATUS_LABELS[nextStatus]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
